import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import HomePage from "@/pages/HomePage";
import EventsPage from "@/pages/EventsPage";
import GamesPage from "@/pages/GamesPage";
import AuthCallback from "@/components/auth/AuthCallback";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { PlatformProvider, usePlatform } from "@/contexts/PlatformContext";
import AppShell from "@/layouts/app/AppShell";
import WebShell from "@/layouts/web/WebShell";
import useSiteSettings from "@/hooks/useSiteSettings";
import useAchievementNotifications from "@/hooks/useAchievementNotifications";
import useBrowserNotifications from "@/hooks/useBrowserNotifications";
import usePushNotifications from "@/hooks/usePushNotifications";
import useUserPreferences from "@/hooks/useUserPreferences";
import { toast } from "sonner";
import { isAdminUser } from "@/lib/userAccess";
import "@/App.css";

const GOOGLE_AUTH_RETURN_PATH_KEY = "lotq_google_auth_return_path";

function extractAuthPayloadFromUrl(url) {
  try {
    const parsedUrl = new URL(url);
    const hashParams = new URLSearchParams(parsedUrl.hash.replace(/^#/, ""));
    return {
      authToken: hashParams.get("auth_token") || parsedUrl.searchParams.get("auth_token"),
      sessionId: hashParams.get("session_id") || parsedUrl.searchParams.get("session_id"),
    };
  } catch {
    const [pathPart = "", hashPart = ""] = url.split("#");
    const hashParams = new URLSearchParams(hashPart);
    const searchPart = pathPart.includes("?") ? pathPart.split("?")[1] : "";
    const searchParams = new URLSearchParams(searchPart);
    return {
      authToken: hashParams.get("auth_token") || searchParams.get("auth_token"),
      sessionId: hashParams.get("session_id") || searchParams.get("session_id"),
    };
  }
}

const PastEventsPage = lazy(() => import("@/pages/PastEventsPage"));
const HirePage = lazy(() => import("@/pages/HirePage"));
const ThemesPage = lazy(() => import("@/pages/ThemesPage"));
const QuestionsPage = lazy(() => import("@/pages/QuestionsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const EstablishmentPage = lazy(() => import("@/pages/EstablishmentPage"));
const PromoterPage = lazy(() => import("@/pages/PromoterPage"));
const CheckoutSuccessPage = lazy(() => import("@/pages/CheckoutSuccessPage"));
const QuizClubPage = lazy(() => import("@/pages/QuizClubPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const MyPlanPage = lazy(() => import("@/pages/MyPlanPage"));
const MyTeamPage = lazy(() => import("@/pages/MyTeamPage"));
const MyQuizzesPage = lazy(() => import("@/pages/MyQuizzesPage"));
const QuizMakerPage = lazy(() => import("@/pages/QuizMakerPage"));
const BenefitsPage = lazy(() => import("@/pages/BenefitsPage"));
const CashbackPage = lazy(() => import("@/pages/CashbackPage"));
const ScoreboardPage = lazy(() => import("@/pages/ScoreboardPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const MyTicketsPage = lazy(() => import("@/pages/MyTicketsPage"));
const SpecialQuizPage = lazy(() => import("@/pages/SpecialQuizPage"));
const LiveEventRoomPage = lazy(() => import("@/pages/LiveEventRoomPage"));
const LiveEventPresenterPage = lazy(() => import("@/pages/LiveEventPresenterPage"));
const LiveEventOverlayPage = lazy(() => import("@/pages/LiveEventOverlayPage"));
const LiveGameJoinPage = lazy(() => import("@/pages/LiveGameJoinPage"));
const QuizNoSeuEventoPage = lazy(() => import("@/pages/QuizNoSeuEventoPage"));
const QuizNoSeuEventoDeckPreviewPage = lazy(() => import("@/pages/QuizNoSeuEventoDeckPreviewPage"));
const QuizNoSeuEventoPresenterConsolePage = lazy(() => import("@/pages/QuizNoSeuEventoPresenterConsolePage"));
const PresenterDashboardPage = lazy(() => import("@/pages/PresenterDashboardPage"));
const PresenterStagePage = lazy(() => import("@/pages/PresenterStagePage"));
const QuizNoSeuEventoReportPage = lazy(() => import("@/pages/QuizNoSeuEventoReportPage"));
const QuizNoSeuEventoMaterialsPage = lazy(() => import("@/pages/QuizNoSeuEventoMaterialsPage"));
const QuizNoSeuEventoEditorialPage = lazy(() => import("@/pages/QuizNoSeuEventoEditorialPage"));
const DesafioFazGameWordCloudStagePage = lazy(() => import("@/pages/DesafioFazGameWordCloudStagePage"));
const DesafioFazGameQaStagePage = lazy(() => import("@/pages/DesafioFazGameQaStagePage"));
const DesafioFazGameMetaCallbackPage = lazy(() => import("@/pages/DesafioFazGameMetaCallbackPage"));
const EventFeedbackPage = lazy(() => import("@/pages/EventFeedbackPage"));

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousRestoration;
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.hash) return;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, [location.pathname, location.search, location.hash]);

  return null;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loginWithGoogle, acceptExternalToken } = useAuth();
  const { isApp } = usePlatform();
  const isStageRoute = /^\/contratar\/on-demand\/projetos\/[^/]+\/(apresentacao|apresentador)$/.test(location.pathname)
    || /^\/sala-evento\/overlay$/.test(location.pathname)
    || /^\/sala-evento\/[^/]+\/overlay$/.test(location.pathname)
    || /^\/apresentador\/eventos\/[^/]+\/(qr|podium)$/.test(location.pathname)
    || /^\/(?:admin\/)?desafio-fazgame\/(nuvem|perguntas)$/.test(location.pathname);
  const isOverflowLockedStageRoute = /^\/contratar\/on-demand\/projetos\/[^/]+\/apresentacao$/.test(location.pathname)
    || /^\/sala-evento\/overlay$/.test(location.pathname)
    || /^\/sala-evento\/[^/]+\/overlay$/.test(location.pathname)
    || /^\/apresentador\/eventos\/[^/]+\/(qr|podium)$/.test(location.pathname)
    || /^\/(?:admin\/)?desafio-fazgame\/(nuvem|perguntas)$/.test(location.pathname);
  
  // Load site settings and update document title/meta
  useSiteSettings();
  useUserPreferences();
  
  // Achievement notifications
  useAchievementNotifications(user);
  useBrowserNotifications(user);
  usePushNotifications(user);
  
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    if (isOverflowLockedStageRoute) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
    };
  }, [isOverflowLockedStageRoute]);

  useEffect(() => {
    if (!isApp || !Capacitor.isNativePlatform()) {
      return undefined;
    }

    let listenerHandle;
    const handledUrls = new Set();

    const handleNativeUrl = async (url) => {
      if (!url || handledUrls.has(url)) {
        return;
      }
      handledUrls.add(url);

      const { authToken, sessionId } = extractAuthPayloadFromUrl(url);
      if (!authToken && !sessionId) {
        return;
      }

      try {
        // The custom tab may still be visible after returning through the deep link.
        await Browser.close().catch(() => undefined);
        if (authToken) {
          await acceptExternalToken(authToken);
        } else {
          await loginWithGoogle(sessionId);
        }
        const returnPath = window.localStorage.getItem(GOOGLE_AUTH_RETURN_PATH_KEY) || "/";
        window.localStorage.removeItem(GOOGLE_AUTH_RETURN_PATH_KEY);
        navigate(returnPath, { replace: true });
      } catch (error) {
        console.error("Native Google auth error:", error);
        window.localStorage.removeItem(GOOGLE_AUTH_RETURN_PATH_KEY);
        toast.error("Erro ao entrar com Google. Tente novamente.");
        navigate("/", { replace: true });
      }
    };

    const attachListener = async () => {
      listenerHandle = await CapacitorApp.addListener("appUrlOpen", ({ url }) => {
        void handleNativeUrl(url);
      });

      // Android can relaunch the app with the callback instead of emitting appUrlOpen.
      const launchResult = await CapacitorApp.getLaunchUrl();
      if (launchResult?.url) {
        await handleNativeUrl(launchResult.url);
      }
    };

    void attachListener();

    return () => {
      if (listenerHandle?.remove) {
        listenerHandle.remove();
      }
    };
  }, [acceptExternalToken, isApp, loginWithGoogle, navigate]);

  // Check URL fragment for auth callback data.
  // This must be checked synchronously during render to prevent race conditions.
  if (location.hash?.includes('session_id=') || location.hash?.includes('auth_token=')) {
    return <AuthCallback />;
  }

  const routeFallback = (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#fccf1f] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (isStageRoute) {
    return (
      <>
        <ScrollManager />
        <Suspense fallback={routeFallback}>
          <Routes>
            <Route path="/apresentador/eventos/:eventId/qr" element={<PresenterStagePage mode="qr" />} />
            <Route path="/apresentador/eventos/:eventId/podium" element={<PresenterStagePage mode="podium" />} />
            <Route
              path="/contratar/on-demand/projetos/:projectId/apresentacao"
              element={(
                <AdminOnlyRoute fallback={routeFallback}>
                  <QuizNoSeuEventoDeckPreviewPage />
                </AdminOnlyRoute>
              )}
            />
            <Route
              path="/contratar/on-demand/projetos/:projectId/apresentador"
              element={(
                <AdminOnlyRoute fallback={routeFallback}>
                  <QuizNoSeuEventoPresenterConsolePage />
                </AdminOnlyRoute>
              )}
            />
            <Route
              path="/sala-evento/:eventId/overlay"
              element={<LiveEventOverlayPage />}
            />
            <Route
              path="/sala-evento/overlay"
              element={<LiveEventOverlayPage />}
            />
            <Route
              path="/desafio-fazgame/nuvem"
              element={<DesafioFazGameWordCloudStagePage />}
            />
            <Route
              path="/desafio-fazgame/perguntas"
              element={<DesafioFazGameQaStagePage />}
            />
            <Route
              path="/admin/desafio-fazgame/nuvem"
              element={<DesafioFazGameWordCloudStagePage />}
            />
            <Route
              path="/admin/desafio-fazgame/perguntas"
              element={<DesafioFazGameQaStagePage />}
            />
          </Routes>
        </Suspense>
      </>
    );
  }

  const routeContent = (
    <>
      <ScrollManager />
      <Suspense fallback={routeFallback}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/eventos" element={<EventsPage />} />
          <Route path="/checkout/retomar/:resumeToken" element={<EventsPage />} />
          <Route path="/eventos/:slug" element={<EventsPage />} />
          <Route path="/eventos/passados" element={<PastEventsPage />} />
          <Route path="/avaliar" element={<EventFeedbackPage />} />
          <Route path="/contratar" element={<HirePage />} />
          <Route
            path="/contratar/on-demand"
            element={(
              <AdminOnlyRoute fallback={routeFallback}>
                <QuizNoSeuEventoPage key="quiz-dashboard" />
              </AdminOnlyRoute>
            )}
          />
          <Route
            path="/contratar/on-demand/novo"
            element={(
              <AdminOnlyRoute fallback={routeFallback}>
                <QuizNoSeuEventoPage key="quiz-create" mode="create" />
              </AdminOnlyRoute>
            )}
          />
          <Route
            path="/contratar/on-demand/projetos/:projectId/apresentacao"
            element={(
              <AdminOnlyRoute fallback={routeFallback}>
                <QuizNoSeuEventoDeckPreviewPage />
              </AdminOnlyRoute>
            )}
          />
          <Route
            path="/contratar/on-demand/projetos/:projectId/apresentador"
            element={(
              <AdminOnlyRoute fallback={routeFallback}>
                <QuizNoSeuEventoPresenterConsolePage />
              </AdminOnlyRoute>
            )}
          />
          <Route
            path="/contratar/on-demand/projetos/:projectId/relatorio"
            element={(
              <AdminOnlyRoute fallback={routeFallback}>
                <QuizNoSeuEventoReportPage />
              </AdminOnlyRoute>
            )}
          />
          <Route
            path="/contratar/on-demand/projetos/:projectId/materiais"
            element={(
              <AdminOnlyRoute fallback={routeFallback}>
                <QuizNoSeuEventoMaterialsPage />
              </AdminOnlyRoute>
            )}
          />
          <Route
            path="/contratar/on-demand/projetos/:projectId/editorial"
            element={(
              <AdminOnlyRoute fallback={routeFallback}>
                <QuizNoSeuEventoEditorialPage />
              </AdminOnlyRoute>
            )}
          />
          <Route path="/jogos" element={<GamesPage />} />
          <Route path="/temas" element={<ThemesPage />} />
          <Route path="/perguntas" element={<QuestionsPage />} />
          <Route path="/sobre" element={<AboutPage />} />
          <Route path="/clube" element={<QuizClubPage />} />
          <Route path="/perfil" element={<ProfilePage />} />
          <Route path="/meus-ingressos" element={<MyTicketsPage />} />
          <Route path="/meu-plano" element={<MyPlanPage />} />
          <Route path="/minha-equipe" element={<MyTeamPage />} />
          <Route path="/quizmaker" element={<QuizMakerPage />} />
          <Route path="/meus-quizzes" element={<MyQuizzesPage />} />
          <Route path="/meus_quizzes" element={<MyQuizzesPage />} />
          <Route path="/beneficios" element={<BenefitsPage />} />
          <Route path="/cashback" element={<CashbackPage />} />
          <Route path="/configuracao" element={<SettingsPage />} />
          <Route path="/placar" element={<ScoreboardPage />} />
          <Route path="/termos" element={<TermsPage />} />
          <Route path="/termos-e-condicoes" element={<TermsPage />} />
          <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
          <Route path="/suporte" element={<SupportPage />} />
          <Route path="/faq" element={<SupportPage />} />
          <Route path="/notificacoes" element={<NotificationsPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/quiz-especial/:eventId" element={<SpecialQuizPage />} />
          <Route path="/sala-evento/:eventId" element={<LiveEventRoomPage />} />
          <Route
            path="/sala-evento/:eventId/apresentador"
            element={(
              <AdminOnlyRoute fallback={routeFallback}>
                <LiveEventPresenterPage />
              </AdminOnlyRoute>
            )}
          />
          <Route path="/entrar-sala" element={<LiveGameJoinPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route
            path="/fazgame/meta/callback"
            element={<DesafioFazGameMetaCallbackPage />}
          />
          <Route
            path="/desafio-fazgame/nuvem"
            element={<DesafioFazGameWordCloudStagePage />}
          />
          <Route
            path="/desafio-fazgame/perguntas"
            element={<DesafioFazGameQaStagePage />}
          />
          <Route
            path="/admin/desafio-fazgame/nuvem"
            element={<DesafioFazGameWordCloudStagePage />}
          />
          <Route
            path="/admin/desafio-fazgame/perguntas"
            element={<DesafioFazGameQaStagePage />}
          />
          <Route path="/promotor" element={<PromoterPage />} />
          <Route path="/estabelecimento" element={<EstablishmentPage />} />
          <Route path="/apresentador" element={<PresenterDashboardPage />} />
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
        </Routes>
      </Suspense>
    </>
  );

  return isApp ? (
    <AppShell>{routeContent}</AppShell>
  ) : (
    <WebShell>{routeContent}</WebShell>
  );
}

function AdminOnlyRoute({ children, fallback }) {
  const { user, loading } = useAuth();
  const isAdmin = isAdminUser(user);

  if (loading) {
    return fallback || (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#fccf1f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  useEffect(() => {
    const removeEmergentBadge = () => {
      const selectors = [
        "#emergent-badge",
        'a[href*="app.emergent.sh/?utm_source=emergent-badge"]',
        'a[href*="app.emergent.sh"]',
      ];

      selectors.forEach((selector) => {
        document.querySelectorAll(selector).forEach((node) => node.remove());
      });
    };

    removeEmergentBadge();

    const observer = new MutationObserver(() => {
      removeEmergentBadge();
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <PlatformProvider>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </PlatformProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
